import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { downloadOutreachAttachment } from '@discovr/features';
import type { OutreachMessage } from '@discovr/types';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { fmtDateTime } from '../../utils/campaignDetailHelpers';
import { FilePdf, FileImage, FileXls, FileDoc, FileText } from '../../../design-system/icons';
import { IntentPill } from './IntentPill';
import { QuoteBlock, hasQuoteContent } from './QuoteBlock';
import { matchConfidenceHelp } from './matchConfidence';
import { formatBytes } from './attachmentFormat';
import { InMessageTag } from './InMessageTag';
import { RichBody, splitQuotedHistory } from './richBody';

// Figma "Attach / File Chip" (614:52128): a tinted type tile per kind, then the name and "Type · size".
const FILE_KINDS = {
  pdf: { label: 'PDF', tile: 'bg-red-100 text-red-600', Icon: FilePdf },
  image: { label: 'Image', tile: 'bg-violet-100 text-violet-600', Icon: FileImage },
  spreadsheet: { label: 'Spreadsheet', tile: 'bg-[#e7f2e7] text-emerald-600', Icon: FileXls },
  document: { label: 'Document', tile: 'bg-sky-100 text-sky-600', Icon: FileDoc },
  file: { label: 'File', tile: 'bg-neutral-100 text-neutral-700', Icon: FileText },
} as const;

function fileKind(raw: string): keyof typeof FILE_KINDS {
  const contentType = (raw.split(';')[0] ?? '').trim().toLowerCase();
  if (contentType === 'application/pdf') return 'pdf';
  if (contentType.startsWith('image/')) return 'image';
  if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'text/csv') return 'spreadsheet';
  if (contentType.includes('word')) return 'document';
  return 'file';
}


const squash = (text: string) => text.replace(/\s+/g, ' ').trim();

export interface InboundMessageCardProps {
  message: OutreachMessage;
  entryId: string;
  canWrite: boolean;
  /** Hosted inside a ThreadBubble (Section D) — the bubble's own meta line
   *  already carries the sender + timestamp, so this hides just that pair
   *  (intent/confidence stay: they aren't sender/timestamp duplicates). */
  inBubble?: boolean;
  onAttachUnmatched?: () => void;
}

export function InboundMessageCard({ message, entryId, canWrite, inBubble, onAttachUnmatched }: InboundMessageCardProps) {
  const [showQuoted, setShowQuoted] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const download = async (attachmentId: number, filename: string) => {
    setDownloadingId(attachmentId);
    try {
      await downloadOutreachAttachment(entryId, attachmentId, filename);
    } catch {
      toast.error("Couldn't download this file — try again");
    } finally {
      setDownloadingId(null);
    }
  };
  // A bounce report reads as its text; any other mail with an HTML part renders formatted.
  const rich = useMemo(() => (message.bodyHtml && !message.isDsn ? splitQuotedHistory(message.bodyHtml) : null), [message.bodyHtml, message.isDsn]);
  const text = showQuoted ? message.bodyText : (message.visibleText ?? message.bodyText);
  const hasQuoted = rich ? rich.hasQuoted : !!message.bodyText && !!message.visibleText && squash(message.bodyText) !== squash(message.visibleText);
  const bodyClass = cn('mt-3 text-foreground', inBubble ? 'text-base/6' : 'text-body-sm-regular');
  const tone = message.isDsn ? (message.dsn?.kind === 'hard' ? 'border-rose-300' : 'border-amber-300') : message.isAutoReply ? 'border-border opacity-70' : 'border-border';
  return (
    <article className={cn(inBubble ? 'border-0 bg-transparent p-0' : 'rounded-xl border bg-card p-4', inBubble ? undefined : tone)}>
      <header className="flex flex-wrap items-center gap-2">
        {!inBubble && (
          <>
            <span className="text-body-sm-medium text-foreground">{message.fromAddress ?? 'Unknown sender'}</span>
            {message.receivedAt ? <span className="text-[12px] text-muted-foreground">Received {fmtDateTime(message.receivedAt)}</span> : null}
          </>
        )}
        {/* In a bubble the intent sits in the head row with the sender (ThreadView). */}
        {!inBubble && <IntentPill intent={message.intent} />}
        {/* In a bubble the direction badge carries this as its tooltip instead. */}
        {!inBubble && matchConfidenceHelp(message.matchConfidence) ? <span title={matchConfidenceHelp(message.matchConfidence)} className="rounded-full bg-(--stone-100) px-2 py-[2px] text-[11px] text-muted-foreground">{message.matchConfidence}</span> : null}
      </header>
      {message.isAutoReply ? <p className="mt-2 text-[12px] text-muted-foreground">Automatic reply — follow-ups continue</p> : null}
      {message.isDsn && message.dsn ? <p className={cn('mt-2 text-[12px]', message.dsn.kind === 'hard' ? 'text-rose-700' : 'text-amber-700')}>{message.dsn.kind === 'hard' ? `Bounce · hard ${message.dsn.status} · address suppressed` : message.dsn.kind === 'blocked' ? `Blocked by the receiving server · policy ${message.dsn.status} · address not suppressed, conversation stays open` : message.dsn.kind === 'delayed' ? `Delivery delayed · ${message.dsn.status}` : message.dsn.kind === 'soft' ? `Soft bounce · ${message.dsn.status}` : `Delivery report · ${message.dsn.status}`}</p> : null}
      {message.parseError ? <p className="mt-2 text-[12px] text-rose-700">Could not parse this message ({message.parseError})</p> : null}
      {message.truncated ? <p className="mt-2 text-[12px] text-muted-foreground">Body truncated at the size cap</p> : null}
      {rich ? (
        <RichBody html={showQuoted ? (message.bodyHtml ?? '') : rich.visible} className={bodyClass} />
      ) : text ? (
        <pre className={cn(bodyClass, 'whitespace-pre-wrap font-sans')}>{text}</pre>
      ) : null}
      {message.attachments?.length ? (
        <div className="mt-3 flex flex-col gap-3.5 border-t border-t-neutral-100! pt-3.5">
          <div className="flex flex-wrap gap-3">
            {message.attachments.map((a) => {
              const kind = FILE_KINDS[fileKind(a.contentType)];
              return (
                <button
                  key={a.id}
                  type="button"
                  data-testid="attachment-chip"
                  aria-label={`Download ${a.filename} (${formatBytes(a.sizeBytes)})`}
                  disabled={downloadingId === a.id}
                  onClick={() => void download(a.id, a.filename)}
                  className="flex h-14 max-w-full items-center gap-3 rounded-xl border border-border bg-card py-2 pr-3 pl-2 text-left hover:bg-accent disabled:opacity-60"
                >
                  <span data-part="file-tile" className={cn('flex size-[38px] shrink-0 items-center justify-center rounded-[10px]', kind.tile)}>
                    <kind.Icon size={22} aria-hidden />
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span title={a.filename} className="truncate text-sm/5 font-medium text-foreground">
                      {a.filename}
                    </span>
                    <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                      {`${kind.label} · ${formatBytes(a.sizeBytes)}`}
                      {a.isInline ? <InMessageTag /> : null}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
      {hasQuoted ? (
        <Button type="button" variant="link" size="xs" className="mt-1 h-auto px-0 text-muted-foreground" onClick={() => setShowQuoted((value) => !value)}>
          {showQuoted ? 'Hide quoted history' : 'Show quoted history'}
        </Button>
      ) : null}
      <QuoteBlock parsed={message.parsed} quote={message.quote} canWrite={canWrite} onAttachUnmatched={onAttachUnmatched} />
      {/* Without a quote box the agent's notes would show nowhere; with one they show inside it. */}
      {message.parsed?.notes && !hasQuoteContent(message.parsed, message.quote) ? (
        <p data-testid="parse-notes" className="mt-2 text-[12px] text-muted-foreground">
          {message.parsed.notes}
        </p>
      ) : null}
    </article>
  );
}
